<template>
  <div class="carpet" ref="carpet"></div>
  <div class="carpet-vignette" v-if="vignette"></div>
</template>

<script>
import carpetTile from '@/assets/carpet.jpg';

// Intrinsic size of the seamless tile, used to keep its aspect ratio.
const TILE_W = 448;
const TILE_H = 644;

export default {
  name: 'CarpetBackground',
  props: {
    // Desired on-screen width of one tile, in CSS pixels.
    scale: { type: Number, default: 480 },
    vignette: { type: Boolean, default: true },
  },
  mounted() {
    this.$refs.carpet.style.backgroundImage = `url('${carpetTile}')`;
    this.layout();
    window.addEventListener('resize', this.layout);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.layout);
  },
  watch: {
    scale() { this.layout(); },
  },
  methods: {
    /* Two constraints pull against each other: the weave should look the same
       physical size on a phone and on a 4K monitor, but a whole number of tiles
       must span the width or the right edge shows a chopped-off tile.
       So round to the nearest tile count, then divide the viewport by it exactly.
       The pattern breathes a few percent instead of the grid breaking. */
    layout() {
      const vw = document.documentElement.clientWidth;
      const across = Math.max(1, Math.round(vw / this.scale));
      const tileWidth = vw / across;

      this.$refs.carpet.style.backgroundSize =
        `${tileWidth}px ${tileWidth * (TILE_H / TILE_W)}px`;
    },
  },
};
</script>

<style scoped>
/* The floor scrolls with the page as an ordinary background rather than being
   moved by script. Driving background-position from a scroll handler forces a
   repaint every frame and always trails the content by one, which reads as
   flicker; letting the browser scroll it is exact and costs nothing.
   The layer spans the whole document, so the tiling never runs out. */
.carpet {
  position: absolute;
  inset: 0;
  z-index: -2;
  background-repeat: repeat;
}

/* The vignette is light, not floor, so it stays put while the weave moves. */
.carpet-vignette {
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background: radial-gradient(ellipse at 50% 40%, transparent 45%, rgba(60, 44, 30, 0.28) 100%);
}
</style>
